import { useEffect, useState } from "react";

/**
 * ImpersonationBanner
 * ─────────────────────────────────────────────────────────────
 * Drop this directly above the customer CRM frame when an
 * internal operator is acting as a customer user. The red top
 * border and pulsing dot are intentional: there is no ambiguity
 * about whether you're in your own session or someone else's.
 *
 * The banner is dumb. Hand it a tenant + reason; wire
 * `onExit` to drop the impersonation token and route back to
 * Operator Console → the same tenant detail.
 */

export interface ImpersonationBannerProps {
  /** Customer workspace being impersonated. */
  tenantName: string;
  /** The user inside that workspace you're acting as. */
  asUser: string;
  /** Justification text — ticket number, reason. Shown verbatim. */
  reason?: string;
  /** Optional ISO start timestamp to drive the elapsed counter. */
  sessionStartedAt?: string;
  /** Drop the session and return to Operator. Required. */
  onExit: () => void;
  /** Optional toggle into read-only mode. */
  onReadOnly?: () => void;
}

export function ImpersonationBanner({
  tenantName,
  asUser,
  reason,
  sessionStartedAt,
  onExit,
  onReadOnly,
}: ImpersonationBannerProps) {
  const elapsed = useElapsed(sessionStartedAt);

  return (
    <div role="status" aria-live="polite" className="rr-imp-banner">
      <span className="rr-imp-dot" aria-hidden="true" />
      <div className="rr-imp-text">
        <strong>
          You are impersonating {asUser} at {tenantName}
        </strong>
        <span>
          Every action is logged
          {reason && <> · {reason}</>}
          {elapsed && <> · {elapsed} elapsed</>}
        </span>
      </div>
      <div className="rr-imp-spacer" />
      {onReadOnly && (
        <button type="button" className="rr-imp-btn rr-imp-btn--ghost" onClick={onReadOnly}>
          View read-only
        </button>
      )}
      <button type="button" className="rr-imp-btn" onClick={onExit}>
        Exit impersonation
      </button>
    </div>
  );
}

function useElapsed(start?: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!start) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [start]);
  if (!start) return null;
  const ms = Math.max(0, now - new Date(start).getTime());
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
